/**
 * Экран «Поставщики» (подрядчики) — tenant-app, волна 3 «Облака».
 *
 * Дизайн: manager v3 (lovart-hotels-design-manager-v3-2026-06-06.html),
 * Analytics → «Vendors»: реестр поставщиков отеля карточками с аватаром,
 * названием, кодом, статусом проверки (KYC-бейдж) и контактами. Фильтр-чипы
 * по состоянию проверки.
 *
 * Реальный русский экран поверх готового бэка GET /v1/vendors, а НЕ
 * англ-заглушка «Vendor».
 *
 * i18n: 100% RU — состояния KYC через vendors-format (не сырые англ-enum).
 * Мобайл 375: одноколоночный stack, чипы переносятся.
 */
import { useMemo, useState } from 'react';
import { Card, Skeleton } from '@hsc/ui';
import {
  KYC_FILTERS,
  kycStateLabel,
  kycStateTone,
  vendorInitials,
} from './vendors-format.js';
import { useVendors } from './useVendors.js';

export function VendorsPage(): JSX.Element {
  const [state, setState] = useState('');
  const { data, isLoading, isError } = useVendors(state || undefined);

  const vendors = useMemo(() => data?.items ?? [], [data]);

  return (
    <section data-testid="vendors-page">
      <h1 className="hsc-page__title">Поставщики</h1>
      <p className="ta-muted">
        Подрядчики и поставщики отеля. Статус показывает результат проверки документов (KYC).
      </p>

      <div className="vnd-filters" role="tablist" aria-label="Фильтр по статусу" data-testid="vendors-filters">
        {KYC_FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            role="tab"
            aria-selected={state === f.value}
            className={`ta-chip vnd-chip${state === f.value ? ' is-active' : ''}`}
            data-testid={`vendors-filter-${f.value || 'all'}`}
            onClick={() => setState(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="hsc-stack" data-testid="vendors-loading">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={84} radius={16} />
          ))}
        </div>
      ) : isError || !data ? (
        <Card>
          <p role="alert">Не удалось загрузить список поставщиков. Попробуйте обновить страницу.</p>
        </Card>
      ) : vendors.length === 0 ? (
        <Card>
          <p data-testid="vendors-empty">
            {state
              ? 'По выбранному фильтру поставщиков нет.'
              : 'Поставщики ещё не заведены. Они появятся здесь после добавления подрядчиков отеля.'}
          </p>
        </Card>
      ) : (
        <ul className="hsc-stack" data-testid="vendors-list">
          {vendors.map((v) => (
            <li key={v.id} data-testid={`vendor-${v.id}`}>
              <Card>
                <div className="vnd-row">
                  <span className="vnd-avatar" aria-hidden="true">
                    {vendorInitials(v.display_name)}
                  </span>
                  <div className="vnd-row__main">
                    <h2 className="hsc-card__title vnd-name">{v.display_name}</h2>
                    <p className="ta-muted vnd-meta">
                      <span className="vnd-code">{v.code}</span>
                      {v.contact_email && (
                        <>
                          {' · '}
                          <a href={`mailto:${v.contact_email}`}>{v.contact_email}</a>
                        </>
                      )}
                      {v.contact_phone && (
                        <>
                          {' · '}
                          <a href={`tel:${v.contact_phone}`}>{v.contact_phone}</a>
                        </>
                      )}
                    </p>
                    {v.kyc_state === 'rejected' && v.kyc_reject_reason && (
                      <p className="vnd-reason">Причина отказа: {v.kyc_reject_reason}</p>
                    )}
                    {v.kyc_state === 'suspended' && v.suspension_reason && (
                      <p className="vnd-reason">Приостановлен: {v.suspension_reason}</p>
                    )}
                  </div>
                  <span
                    className={`fleet-badge fleet-badge--${kycStateTone(v.kyc_state)}`}
                    data-testid={`vendor-state-${v.id}`}
                  >
                    {kycStateLabel(v.kyc_state)}
                  </span>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
